import { useMutation } from "@tanstack/react-query";
import { uniq } from "lodash";
import api from "../../../services/api";
import { show } from "../../../notifications/notifications";
import { NotificationsLevel } from "../../../lib/enums";

const HASHES_LENGTH_LIMIT = 100;

const useHashUnsubscriber = (hashes: Array<string>, onClose: () => void) => {
  const uniqueHashes = uniq(hashes);
  const hasDuplicates = uniqueHashes.length < hashes.length;
  const isHashListOverLimit = uniqueHashes.length > HASHES_LENGTH_LIMIT;
  const cappedHashes = isHashListOverLimit
    ? uniqueHashes.slice(0, HASHES_LENGTH_LIMIT)
    : uniqueHashes;
  const hasMultipleHashes = cappedHashes.length > 1;

  const { mutate } = useMutation({
    mutationFn: () => api.unsubscribeHashes(cappedHashes),
    // 200 even when nothing was unsubscribed; `unsubscribed` says what was.
    onSuccess: response => {
      onClose();
      const unsubscribed = response?.data?.unsubscribed ?? [];

      if (!unsubscribed.length) {
        show({
          level: NotificationsLevel.INFO,
          message: `Selected SHA1 hash${
            hasMultipleHashes ? "es were" : " was"
          } not subscribed to any alert.`
        });
        return;
      }

      const isPartial = unsubscribed.length < cappedHashes.length;
      show({
        level: NotificationsLevel.SUCCESS,
        message: isPartial
          ? `Unsubscribed ${unsubscribed.length} of ${cappedHashes.length} selected SHA1 hashes; the rest were not subscribed to any alert.`
          : `Successfully unsubscribed from selected SHA1 hash${
              hasMultipleHashes ? "es" : ""
            }.`
      });
    },
    onError: () => {
      show({
        level: NotificationsLevel.ERROR,
        message: "Something went wrong while trying to unsubscribe..."
      });
    }
  });

  const getDialogMessage = () => {
    const countText = hasMultipleHashes
      ? `these hashes (${cappedHashes.length} total)`
      : "this hash";
    const duplicateText = hasDuplicates ? " (duplicates removed)" : "";

    return `Are you sure you want to unsubscribe ${countText} from all alert subscriptions?${duplicateText}`;
  };

  return {
    unsubscribeAction: mutate,
    cappedHashes,
    dialogMessage: getDialogMessage(),
    isHashListOverLimit
  };
};

export default useHashUnsubscriber;
